import { Document, Packer } from "docx";
import { supabase } from "@/integrations/supabase/client";
import { consumeFreeQuota, FREE_QUOTA_MESSAGE } from "@/lib/plan/freeTier";

import { trackUsage } from "@/lib/usage/track";
import { dispatchWebhookEvent } from "@/lib/integrations/webhooks";
import { pushDocumentToDestination } from "@/lib/integrations/destinations";
import { createStamp, type DocumentStamp } from "./watermark";
import { getActivePhase } from "@/hooks/useCurrentPhase";
import { toast } from "sonner";

/** Kontrollerar dagligt exporttak. Vid fel släpper vi igenom exporten. */
export const checkExportQuota = async (): Promise<{
  allowed: boolean;
  used: number;
  quota: number;
}> => {
  try {
    const { data, error } = await supabase.rpc("check_export_quota");
    const row = (data as unknown as { allowed: boolean; used: number; quota: number }[] | null)?.[0];
    if (error || !row) return { allowed: true, used: 0, quota: 0 };
    return row;
  } catch {
    return { allowed: true, used: 0, quota: 0 };
  }
};


export interface ExportHistoryMeta {
  /** Stabil nyckel för dokumenttypen, t.ex. "amp", "skyddsrond" */
  documentType: string;
  /** Dokumentnummer enligt Bas-processen, t.ex. "12" */
  docNumber?: string;
  /** Id på det specifika dokumentet (rad-id) om det finns */
  documentId?: string | null;
  /** Namn som visas i historiken */
  documentName?: string | null;
  /** Projekt-id – hämtas annars från URL:en */
  projectId?: string | null;
  /** Uppgifterna som användes vid exporten */
  snapshot?: unknown;
  /** Vattenstämpeln som trycktes in i dokumentet (skapas annars automatiskt) */
  stamp?: DocumentStamp | null;
}

export interface ExportHistoryEntry {
  id: string;
  version: number;
  filename: string;
  document_name: string | null;
  document_type: string;
  document_id: string | null;
  snapshot: Record<string, unknown> | null;
  exported_by: string | null;
  created_at: string;
  verification_code?: string | null;
  revoked_at?: string | null;
  /** Skedet exporten gjordes i */
  phase_key?: string | null;
}

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/** Projekt-id ligger som första UUID i de flesta appadresser (/uppdrag/:projektId/...) */
export const resolveProjectIdFromUrl = (): string | null => {
  if (typeof window === "undefined") return null;
  const match = window.location.pathname.match(UUID_RE);
  return match ? match[0] : null;
};

const triggerBrowserDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/* ------------------------------------------------------------------
 * Samlad export ("Ladda ner alla dokument")
 * När en insamling är aktiv laddas filerna inte ner var för sig – de
 * fångas upp och paketeras istället i en ZIP av anroparen.
 * ---------------------------------------------------------------- */
export interface CapturedExportFile {
  filename: string;
  blob: Blob;
  documentType: string;
  docNumber: string | null;
  documentName: string | null;
}

let captureSink: CapturedExportFile[] | null = null;

export const isExportCaptureActive = (): boolean => captureSink !== null;

export const beginExportCapture = (): void => {
  captureSink = [];
};

export const endExportCapture = (): CapturedExportFile[] => {
  const files = captureSink ?? [];
  captureSink = null;
  return files;
};


/** Trimmar bort tunga fält (bilder/base64) så historiken hålls liten */
const sanitizeSnapshot = (value: unknown): unknown => {
  const seen = new WeakSet();
  const walk = (input: unknown): unknown => {
    if (typeof input === "string") {
      if (input.length > 2000 || input.startsWith("data:")) return `[${input.length} tecken]`;
      return input;
    }
    if (Array.isArray(input)) return input.slice(0, 200).map(walk);
    if (input && typeof input === "object") {
      if (seen.has(input as object)) return "[cirkulär]";
      seen.add(input as object);
      return Object.fromEntries(
        Object.entries(input as Record<string, unknown>).map(([k, v]) => [k, walk(v)])
      );
    }
    return input ?? null;
  };
  try {
    return walk(value);
  } catch {
    return null;
  }
};

const nextVersion = async (
  projectId: string | null,
  documentType: string,
  documentId: string | null
): Promise<number> => {
  let query = supabase
    .from("document_exports")
    .select("version")
    .eq("document_type", documentType)
    .order("version", { ascending: false })
    .limit(1);

  query = projectId ? query.eq("project_id", projectId) : query.is("project_id", null);
  query = documentId ? query.eq("document_id", documentId) : query.is("document_id", null);

  const { data } = await query;
  return ((data?.[0]?.version as number | undefined) ?? 0) + 1;
};

/**
 * Packar, laddar ner och loggar en Word-export i versionshistoriken.
 * Loggningen är "best effort" – nedladdningen sker alltid.
 */
export const downloadWordDocument = async (
  doc: Document,
  filename: string,
  meta: ExportHistoryMeta
): Promise<number | null> => {
  const quota = await checkExportQuota();
  if (!quota.allowed) {
    toast.error("Dagens exporttak är nått", {
      description: `Du har gjort ${quota.used} av ${quota.quota} exporter i dag. Försök igen i morgon eller kontakta oss.`,
    });
    return null;
  }

  const freeQuota = await consumeFreeQuota("export");
  if (!freeQuota.allowed) {
    toast.error("Exportkvoten för provperioden är slut", {
      description: FREE_QUOTA_MESSAGE.export,
    });
    return null;
  }


  const blob = await Packer.toBlob(doc);
  const captured = captureSink !== null;
  if (captured) {
    captureSink!.push({
      filename,
      blob,
      documentType: meta.documentType,
      docNumber: meta.docNumber ?? null,
      documentName: meta.documentName ?? null,
    });
  } else {
    triggerBrowserDownload(blob, filename);
  }


  try {
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth?.user?.id ?? null;
    if (!userId) return null;

    const projectId = meta.projectId ?? resolveProjectIdFromUrl();
    const documentId = meta.documentId ?? null;
    const version = await nextVersion(projectId, meta.documentType, documentId);
    const stamp =
      meta.stamp ??
      createStamp({
        projectId,
        docNumber: meta.docNumber ?? null,
        documentName: meta.documentName ?? null,
      });

    await supabase.from("document_exports").insert({
      project_id: projectId,
      document_type: meta.documentType,
      document_id: documentId,
      document_name: meta.documentName ?? null,
      filename,
      version,
      exported_by: userId,
      phase_key: getActivePhase().key,
      verification_code: stamp.code,
      watermark_meta: {
        docNumber: meta.docNumber ?? null,
        projectName: stamp.projectName,
        exportedBy: stamp.exportedBy || null,
        exportedAt: stamp.exportedAt,
      } as never,
      snapshot: {
        docNumber: meta.docNumber ?? null,
        exportedAt: new Date().toISOString(),
        verificationCode: stamp.code,
        phase: getActivePhase().name,
        data: sanitizeSnapshot(meta.snapshot) ?? null,
      } as never,
    });

    void trackUsage("document_exported", {
      projectId,
      docNumber: meta.docNumber ?? null,
      documentType: meta.documentType,
      metadata: { version, filename },
    });

    void dispatchWebhookEvent({
      projectId,
      eventType: "document.exported",
      payload: {
        document_type: meta.documentType,
        doc_number: meta.docNumber ?? null,
        version,
        filename,
      },
    });

    // Best effort: pusha Word-filen till projektets aktiva auto-push-destination.
    // Vid samlad export hoppar vi över detta för att inte spamma destinationen.
    if (!captured) {
      void pushDocumentToDestination(filename, blob, {
        projectId,
        documentType: meta.documentType,
        docNumber: meta.docNumber ?? null,
      });
    }



    return version;
  } catch {
    return null;
  }
};

export const fetchExportHistory = async (params: {
  documentType: string;
  documentId?: string | null;
  projectId?: string | null;
}): Promise<ExportHistoryEntry[]> => {
  const projectId = params.projectId ?? resolveProjectIdFromUrl();
  let query = supabase
    .from("document_exports")
    .select(
      "id, version, filename, document_name, document_type, document_id, snapshot, exported_by, created_at, verification_code, revoked_at, phase_key"
    )
    .eq("document_type", params.documentType)
    .order("created_at", { ascending: false })
    .limit(50);

  if (projectId) query = query.eq("project_id", projectId);
  if (params.documentId) query = query.eq("document_id", params.documentId);

  const { data, error } = await query;
  if (error) return [];
  return (data || []) as unknown as ExportHistoryEntry[];
};

/** Projektägaren kan ogiltigförklara en export – verifieringssidan visar då att den inte gäller. */
export const setExportRevoked = async (exportId: string, revoked: boolean): Promise<boolean> => {
  const { data: auth } = await supabase.auth.getUser();
  const { error } = await supabase
    .from("document_exports")
    .update({
      revoked_at: revoked ? new Date().toISOString() : null,
      revoked_by: revoked ? auth?.user?.id ?? null : null,
    } as never)
    .eq("id", exportId);
  return !error;
};
